import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { afterNextRender, Component, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';

import { ClientColorSettingsUpdateRequest, ClientTheme } from '../../../../../../core/models/client-settings.model';
import { ClientSettingsService } from '../../../../../../core/services/client-settings.service';
import { Modal } from '../../../../../../shared/components/modal/modal';
import { ConfirmationService } from '../../../../../../shared';

export interface ThemeOption {
  value: ClientTheme;
  labelKey: string;
}

export type ThemeColorControl = 'primaryColor' | 'secondaryColor' | 'accentColor';

/**
 * Retorna o primeiro valor não vazio (ignora null/undefined e strings em branco).
 * Usado no lugar de `||` para evitar o alerta do Sonar sem perder o fallback
 * para string vazia, que `??` sozinho não cobre.
 */
function firstNonEmpty(...values: (string | null | undefined)[]): string {
  for (const value of values) {
    if (value?.trim()) {
      return value;
    }
  }

  return '';
}

interface PreviewPalette {
  background: string;
  surface: string;
  text: string;
  mutedText: string;
  primary: string;
  accent: string;
}

interface RGBColor {
  red: number;
  green: number;
  blue: number;
}

interface InlineStyleSnapshot {
  property: string;
  value: string;
  priority: string;
}

type ComputedColorProperty = 'color' | 'backgroundColor';

@Component({
  selector: 'app-customer-theme-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslocoPipe, NgSelectModule, Modal],
  templateUrl: './customer-theme-modal.html',
})
export class CustomerThemeModal {
  private readonly formBuilder = inject(FormBuilder);

  private readonly activeModal = inject(NgbActiveModal);

  private readonly clientSettingsService = inject(ClientSettingsService);

  private readonly document = inject(DOCUMENT);

  private readonly platformId = inject(PLATFORM_ID);

  private readonly transloco = inject(TranslocoService);

  readonly customerId = signal('');

  readonly errorMessage = signal('');

  readonly isLoading = this.clientSettingsService.isLoading;

  readonly confirmationService = inject(ConfirmationService);

  private loadedCustomerId = '';

  /**
   * Paleta do tema padrão.
   *
   * Os valores são preenchidos em tempo de execução
   * a partir das classes de utilities.scss.
   */
  private readonly currentThemePalette = signal<PreviewPalette>({
    background: '',
    surface: '',
    text: '',
    mutedText: '',
    primary: '',
    accent: '',
  });

  /**
   * Variáveis aplicadas pelo ClientThemeService que
   * podem alterar o resultado das classes utilitárias.
   *
   * Elas são removidas apenas durante a leitura da
   * paleta padrão e restauradas logo em seguida.
   */
  private readonly previewThemeProperties = [
    '--color-primary',
    '--color-primary-dark',
    '--color-primary-darker',
    '--color-secondary',
    '--color-accent',
    '--color-badge-default',
    '--color-background',
    '--color-surface',
    '--color-surface-light',
    '--color-surface-lighter',
    '--color-off-white',
    '--color-text-secondary',
  ];

  readonly themeOptions: ThemeOption[] = [
    {
      value: ClientTheme.Light,
      labelKey: 'admin.customers.edit.theme.themes.light',
    },
    {
      value: ClientTheme.Dark,
      labelKey: 'admin.customers.edit.theme.themes.dark',
    },
  ];

  readonly themeForm: FormGroup = this.formBuilder.group({
    defaultTheme: [ClientTheme.Dark, [Validators.required]],

    primaryColor: [null as string | null, [Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],

    secondaryColor: [null as string | null, [Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],

    accentColor: [null as string | null, [Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],
  });

  constructor() {
    effect(() => {
      const clientId = this.customerId().trim();

      if (!clientId || clientId === this.loadedCustomerId) {
        return;
      }

      this.loadedCustomerId = clientId;

      this.loadSettings(clientId);
    });

    afterNextRender(() => {
      this.captureDefaultUtilityPalette();
    });
  }

  private async loadSettings(clientId: string): Promise<void> {
    this.errorMessage.set('');

    const settings = await this.clientSettingsService.getByClientId(clientId);

    if (!settings) {
      this.loadedCustomerId = '';

      this.errorMessage.set(this.transloco.translate('admin.customers.edit.theme.errors.loadVisualSettings'));

      return;
    }

    this.themeForm.patchValue({
      defaultTheme:
        (settings.defaultTheme === ClientTheme.Default ? ClientTheme.Dark : settings.defaultTheme) ?? ClientTheme.Dark,

      primaryColor: settings.primaryColor ?? null,

      secondaryColor: settings.secondaryColor ?? null,

      accentColor: settings.accentColor ?? null,
    });

    this.themeForm.markAsPristine();
    this.themeForm.markAsUntouched();
  }

  async onSubmit(): Promise<void> {
    this.errorMessage.set('');

    if (this.themeForm.invalid) {
      this.themeForm.markAllAsTouched();

      this.errorMessage.set(this.transloco.translate('validation.checkFields'));

      return;
    }

    const clientId = this.customerId().trim();

    if (!clientId) {
      this.errorMessage.set(this.transloco.translate('admin.customers.edit.errors.identifyClient'));

      return;
    }

    const formValue = this.themeForm.getRawValue();

    const request: ClientColorSettingsUpdateRequest = {
      defaultTheme: Number(formValue.defaultTheme) as ClientTheme,

      primaryColor: this.normalizeValue(formValue.primaryColor),

      secondaryColor: this.normalizeValue(formValue.secondaryColor),

      accentColor: this.normalizeValue(formValue.accentColor),
    };

    const result = await this.clientSettingsService.updateClientColorSettings(clientId, request);

    if (!result.success) {
      this.errorMessage.set(
        result.error ?? this.transloco.translate('admin.customers.edit.theme.errors.updateVisualSettings'),
      );

      return;
    }
    if (result.success) {
      this.activeModal.close({
        updated: true,
        customerId: clientId,
        settings: result.data,
      });
      await new Promise((resolve) => setTimeout(resolve, 200));
      await this.confirmationService.showInfo(
        this.transloco.translate('confirmation.themeSaved.title'),
        this.transloco.translate('confirmation.themeSaved.message'),
        'success',
      );
    } else {
      this.errorMessage.set(result.error ?? this.transloco.translate('admin.customers.create.errorDefault'));
    }
  }

  setColor(controlName: ThemeColorControl, event: Event): void {
    const input = event.target as HTMLInputElement;

    const control = this.themeForm.get(controlName);

    control?.setValue(input.value.toUpperCase());

    control?.markAsDirty();
    control?.markAsTouched();
  }

  clearColor(controlName: ThemeColorControl): void {
    const control = this.themeForm.get(controlName);

    control?.setValue(null);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  getPickerValue(controlName: ThemeColorControl): string {
    const value = this.themeForm.get(controlName)?.value;

    if (typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value)) {
      return value;
    }

    /*
     * O input color exige um valor hexadecimal
     * válido mesmo quando não existe cor salva.
     *
     * Este valor não é colocado no FormControl.
     */
    return '#000000';
  }

  getThemeLabel(): string {
    const selectedTheme = this.getSelectedTheme();
    const selectedLabelKey = this.themeOptions.find((option) => option.value === selectedTheme)?.labelKey;

    return this.transloco.translate(selectedLabelKey ?? 'admin.customers.edit.theme.themes.default');
  }

  /**
   * No tema Padrão, lê a classe:
   *
   * .bg-color-default
   */
  getPreviewBackgroundColor(): string {
    switch (this.getSelectedTheme()) {
      case ClientTheme.Light:
        return '#FFFFFF';

      case ClientTheme.Dark:
        return '#0A0A0A';

      case ClientTheme.Default:
      default:
        return this.currentThemePalette().background || 'transparent';
    }
  }

  /**
   * No tema Padrão, lê a classe:
   *
   * .bg-color-surface
   */
  getPreviewSurfaceColor(): string {
    switch (this.getSelectedTheme()) {
      case ClientTheme.Light:
        return '#F4F4F4';

      case ClientTheme.Dark:
        return '#161616';

      case ClientTheme.Default:
      default:
        return this.currentThemePalette().surface || 'transparent';
    }
  }

  /**
   * No tema Padrão, lê a classe:
   *
   * .text-primary-brand
   */
  getPreviewTextColor(): string {
    switch (this.getSelectedTheme()) {
      case ClientTheme.Light:
        return '#212121';

      case ClientTheme.Dark:
        return '#FFFFFF';

      case ClientTheme.Default:
      default:
        return this.currentThemePalette().text || 'currentColor';
    }
  }

  /**
   * A cor dos títulos depende somente do tema.
   *
   * Não deve ser afetada pelas cores primária,
   * secundária ou de destaque.
   */
  getPreviewTitleColor(): string {
    switch (this.getSelectedTheme()) {
      case ClientTheme.Light:
        return '#212121';

      case ClientTheme.Dark:
        return '#FFFFFF';

      case ClientTheme.Default:
      default:
        return this.currentThemePalette().text || '#FFFFFF';
    }
  }

  /**
   * A cor secundária é usada em subtextos,
   * descrições e ações secundárias.
   *
   * Quando não houver personalização, o tema
   * Padrão lê:
   *
   * .text-secondary-brand
   */
  getSecondaryColor(): string {
    const customSecondaryColor = this.getFormColor('secondaryColor');

    if (customSecondaryColor) {
      return customSecondaryColor;
    }

    switch (this.getSelectedTheme()) {
      case ClientTheme.Light:
        return '#6C757D';

      case ClientTheme.Dark:
        return '#A0A0A0';

      case ClientTheme.Default:
      default:
        return this.currentThemePalette().mutedText || 'currentColor';
    }
  }

  /**
   * A cor primária é usada em botões principais,
   * links e ações.
   *
   * Quando não houver personalização, lê:
   *
   * .bg-primary-custom
   */
  getPrimaryColor(): string {
    const customPrimaryColor = this.getFormColor('primaryColor');

    // currentThemePalette() começa com strings vazias ('') antes da paleta ser lida do DOM.
    return firstNonEmpty(customPrimaryColor, this.currentThemePalette().primary, 'currentColor');
  }

  /**
   * A cor de destaque é usada em badges,
   * indicadores e ícones.
   *
   * Quando não houver personalização, lê:
   *
   * .bg-color-surface-light
   */
  getAccentColor(): string {
    const customAccentColor = this.getFormColor('accentColor');

    // Mesmo motivo de getPrimaryColor(): accent pode ser ''.
    return firstNonEmpty(customAccentColor, this.currentThemePalette().accent, this.getPrimaryColor());
  }

  /**
   * Calcula uma cor de texto com contraste
   * adequado em relação à cor de fundo.
   */
  getContrastColor(color: string | null | undefined): string {
    const rgb = this.parseColor(color);

    if (!rgb) {
      return '#FFFFFF';
    }

    const luminance = (rgb.red * 299 + rgb.green * 587 + rgb.blue * 114) / 1000;

    return luminance > 160 ? '#212121' : '#FFFFFF';
  }

  resetThemeForm(): void {
    this.themeForm.patchValue({
      defaultTheme: ClientTheme.Dark,

      primaryColor: null,

      secondaryColor: null,

      accentColor: null,
    });

    this.themeForm.markAsDirty();
    this.themeForm.markAsUntouched();

    /**
     * Garante que a preview volte a utilizar a paleta
     * real do tema padrão, e não as variáveis do tema
     * personalizado atualmente aplicado na página.
     */
    this.captureDefaultUtilityPalette();

    this.errorMessage.set('');
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.themeForm.get(fieldName);

    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  /**
   * Captura a paleta real do tema padrão utilizando
   * apenas classes disponíveis em utilities.scss.
   *
   * Como a página pode estar usando um tema customizado,
   * as variáveis CSS e o atributo data-theme são
   * removidos temporariamente durante a leitura.
   * Depois, o estado visual atual da página é restaurado.
   */
  private captureDefaultUtilityPalette(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const root = this.document.documentElement;

    if (!root) {
      return;
    }

    const hadThemeAttribute = 'theme' in root.dataset;
    const previousTheme = root.dataset['theme'] ?? null;

    const inlineStyleSnapshot: InlineStyleSnapshot[] = this.previewThemeProperties.map((property) => ({
      property,
      value: root.style.getPropertyValue(property),
      priority: root.style.getPropertyPriority(property),
    }));

    try {
      delete root.dataset['theme'];

      this.previewThemeProperties.forEach((property) => {
        root.style.removeProperty(property);
      });

      const background = this.readUtilityColor('bg-color-default', 'backgroundColor');

      const surface = this.readUtilityColor('bg-color-surface', 'backgroundColor');

      const text = this.readUtilityColor('text-primary-brand', 'color');

      const mutedText = this.readUtilityColor('text-secondary-brand', 'color');

      const primary = this.readUtilityColor('bg-primary-custom', 'backgroundColor');

      const accent = this.readUtilityColor('bg-color-surface-light', 'backgroundColor');

      this.currentThemePalette.set({
        background,
        surface,
        text,
        mutedText,
        primary,
        accent,
      });
    } finally {
      inlineStyleSnapshot.forEach(({ property, value, priority }) => {
        if (value) {
          root.style.setProperty(property, value, priority);
        } else {
          root.style.removeProperty(property);
        }
      });

      if (hadThemeAttribute && previousTheme !== null) {
        root.dataset['theme'] = previousTheme;
      } else {
        delete root.dataset['theme'];
      }
    }
  }

  /**
   * Cria um elemento temporário com a classe
   * utilitária e lê a propriedade CSS computada.
   */
  private readUtilityColor(utilityClass: string, property: ComputedColorProperty): string {
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }

    const browserWindow = this.document.defaultView;

    const body = this.document.body;

    if (!browserWindow || !body) {
      return '';
    }

    const probe = this.document.createElement('div');

    probe.className = utilityClass;

    probe.setAttribute('aria-hidden', 'true');

    probe.style.position = 'fixed';
    probe.style.left = '-10000px';
    probe.style.top = '-10000px';
    probe.style.width = '1px';
    probe.style.height = '1px';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';

    body.appendChild(probe);

    const computedStyles = browserWindow.getComputedStyle(probe);

    const value = computedStyles[property];

    probe.remove();

    return this.normalizeComputedColor(value);
  }

  private getSelectedTheme(): ClientTheme {
    const value = Number(this.themeForm.get('defaultTheme')?.value);

    if (value === ClientTheme.Light || value === ClientTheme.Dark) {
      return value;
    }

    return ClientTheme.Default;
  }

  private getFormColor(controlName: ThemeColorControl): string | null {
    const value = this.themeForm.get(controlName)?.value;

    return this.normalizeValue(value);
  }

  private normalizeValue(value: string | null | undefined): string | null {
    const normalized = value?.trim();

    return normalized ? normalized.toUpperCase() : null;
  }

  private normalizeComputedColor(value: string | null | undefined): string {
    const normalized = value?.trim();

    if (
      !normalized ||
      normalized === 'transparent' ||
      normalized === 'rgba(0, 0, 0, 0)' ||
      normalized === 'rgba(0,0,0,0)'
    ) {
      return '';
    }

    return normalized;
  }

  /**
   * Converte cores hexadecimais e RGB para
   * calcular a cor de contraste.
   */
  private parseColor(color: string | null | undefined): RGBColor | null {
    if (!color) {
      return null;
    }

    const normalized = color.trim();

    const hexadecimalMatch = /^#([0-9a-f]{6})$/i.exec(normalized);

    if (hexadecimalMatch) {
      const hexadecimal = hexadecimalMatch[1];

      return {
        red: Number.parseInt(hexadecimal.substring(0, 2), 16),

        green: Number.parseInt(hexadecimal.substring(2, 4), 16),

        blue: Number.parseInt(hexadecimal.substring(4, 6), 16),
      };
    }

    const shortHexadecimalMatch = /^#([0-9a-f]{3})$/i.exec(normalized);

    if (shortHexadecimalMatch) {
      const hexadecimal = shortHexadecimalMatch[1];

      return {
        red: Number.parseInt(`${hexadecimal[0]}${hexadecimal[0]}`, 16),

        green: Number.parseInt(`${hexadecimal[1]}${hexadecimal[1]}`, 16),

        blue: Number.parseInt(`${hexadecimal[2]}${hexadecimal[2]}`, 16),
      };
    }

    const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(normalized);

    if (rgbMatch) {
      return {
        red: Number(rgbMatch[1]),

        green: Number(rgbMatch[2]),

        blue: Number(rgbMatch[3]),
      };
    }

    return null;
  }
}
